/** Dev21 — Couple / Host. Elegant card-based introduction of both partners. */

import React from 'react';
import { motion } from 'motion/react';
import { Heart } from 'lucide-react';
import { Section, SectionHeading, Reveal } from '../components/primitives';
import { DEV21_EASE } from '../theme';
import type { Dev21Content } from '../content';

export function CoupleSection({
  content,
}: {
  content: Dev21Content['couple'];
}) {
  return (
    <Section id="couple" tone="cream">
      <SectionHeading
        label={content.label}
        title={content.title}
        intro={content.intro}
      />

      <div className="relative mt-14 grid gap-8 md:grid-cols-2 md:gap-6 lg:gap-10">
        {content.partners.map((partner, i) => (
          <Reveal key={partner.name} delay={i * 0.12}>
            <motion.article
              whileHover={{ y: -8 }}
              transition={{ duration: 0.4, ease: DEV21_EASE }}
              className="group h-full overflow-hidden rounded-3xl border border-[var(--d21-border)] bg-[var(--d21-surface)] shadow-[0_30px_70px_-40px_rgba(33,26,19,0.5)]"
            >
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={partner.photo}
                  alt={partner.name}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_45%,rgba(20,15,10,0.78)_100%)]" />
                <span className="absolute left-5 top-5 rounded-full bg-white/85 px-3 py-1 text-[0.66rem] font-semibold uppercase tracking-[0.2em] text-[var(--d21-accent)] backdrop-blur">
                  {partner.role}
                </span>
                <h3 className="absolute bottom-4 left-5 right-5 font-serif text-3xl font-semibold text-white">
                  {partner.name}
                </h3>
              </div>
              <div className="p-6 sm:p-7">
                <p className="text-base leading-relaxed text-[var(--d21-muted)]">
                  {partner.about}
                </p>
                {partner.family ? (
                  <p className="mt-4 border-t border-[var(--d21-border)] pt-4 text-sm font-medium text-[var(--d21-ink)]">
                    {partner.family}
                  </p>
                ) : null}
              </div>
            </motion.article>
          </Reveal>
        ))}

        {/* Centre heart connector — desktop only */}
        <div className="pointer-events-none absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 md:block">
          <motion.div
            initial={{ scale: 0, rotate: -30 }}
            whileInView={{ scale: 1, rotate: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: DEV21_EASE }}
            className="flex h-16 w-16 items-center justify-center rounded-full border border-[var(--d21-border)] bg-[var(--d21-surface)] shadow-lg"
          >
            <Heart size={24} className="fill-[var(--d21-accent)] text-[var(--d21-accent)]" />
          </motion.div>
        </div>
      </div>
    </Section>
  );
}
